import { Shield, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import BrandLogo from 'src/shared/components/BrandLogo';

type LoadingStage = 'auth' | 'loading' | 'ready';

interface SessionLoadingScreenProps {
  stage?: LoadingStage;
  planName?: string;
  onFadeComplete?: () => void;
}

const getStageMessages = (planName?: string): Record<LoadingStage, string> => {
  const isPro = planName === 'professional';
  return {
    auth: 'Securing your session',
    loading: isPro ? 'Loading your Pro workspace' : 'Loading your workspace',
    ready: isPro ? 'Welcome back' : 'Almost ready',
  };
};

const PLAN_SHIMMER: Record<string, string> = {
  professional: 'from-primary via-primary-hover to-primary-light',
  starter: 'from-primary via-primary-hover to-primary',
};

const SessionLoadingScreen: React.FC<SessionLoadingScreenProps> = ({ stage = 'auth', planName, onFadeComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [displayStage, setDisplayStage] = useState(stage);

  const messages = getStageMessages(planName);
  const shimmer = PLAN_SHIMMER[planName || ''] || PLAN_SHIMMER.starter;
  const isPro = planName === 'professional';
  const PlanIcon = isPro ? Shield : Zap;

  useEffect(() => {
    if (stage === 'ready') {
      const timer = setTimeout(() => {
        setFadeOut(true);
        if (onFadeComplete) {
          setTimeout(onFadeComplete, 600);
        }
      }, 400);
      return () => clearTimeout(timer);
    }
    setDisplayStage(stage);
  }, [stage, onFadeComplete]);

  return (
    <div
      className={`fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-opacity duration-500 ${fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
    >
      <div className="flex flex-col items-center gap-10">
        <div className="animate-[pulse_2.5s_ease-in-out_infinite]">
          <BrandLogo variant="auto" size="lg" />
        </div>

        <div className="flex flex-col items-center gap-5">
          <div className="relative w-48 h-[2px] bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`absolute inset-0 bg-gradient-to-r ${shimmer} rounded-full animate-[shimmer_1.8s_ease-in-out_infinite]`}
            />
          </div>

          <div key={displayStage} className="flex items-center gap-2 animate-[fadeUp_0.4s_ease-out]">
            {planName && displayStage !== 'auth' && <PlanIcon className="w-3.5 h-3.5 text-slate-400" />}
            <p className="text-sm font-medium tracking-wide text-slate-400 dark:text-slate-500">
              {messages[displayStage]}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default SessionLoadingScreen;
